import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dropdownOptions, links, spotterLink } from './constants';
import { fetchPreauthInfoService, getInfoObject } from '../../../utils/tokenService';
import { Horizontal, Vertical } from 'packages/radiant-react/layout/flex-layout/flex-layout';
import { Select } from 'packages/radiant-react/select';
import styles from './style.module.scss';
import {
  BACK_BUTTON_TEXT,
  SELECT_A_COMPONENT,
  SELECT_A_DATA_SOURCE,
  SELECT_A_LIVEBOARD,
  SELECT_A_SAVED_ANSWER,
  SELECT_A_VISUALIZATION,
  SELECT_SAVED_ANSWER_OR_DATA_SOURCE,
  SET_ACTIVE_LEFT_NAVIGATION_ITEM,
} from '../../../constants';
import { Typography, Colors } from 'packages/radiant-react/typography';
import { Icon, IconID, IconSize } from 'packages/radiant-react/icon';
import Separator from '../../UI/Separator';
import { GeneralizedDropdown } from '../../UI/ComponentDropdown/GeneralizedDropdown';
import { isNewVersion, isAiMode } from '../../../utils';
import { Page } from '@thoughtspot/visual-embed-sdk';
import { useAppContext } from 'context/AppContext';

type EmbedSelectionChangeHandler = (selections: EmbedSelections) => void;

type viewType = 'search' | 'liveboard' | 'viz' | 'spotter' | 'home';

interface EmbedSelections {
  pinboard?: string;
  viz?: string;
  dataSource?: string;
  answerId?: string;
  searchType?: 'datasource' | 'answers';
  pageId?: string;
  spotter?: string;
}

interface HeaderProps {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  selectedPinboard?: string;
  selectedViz?: string;
  selectedDataSource?: string;
  selectedAnswer?: string;
  selectedFullApp?: string;
  onEmbedSelectionChange?: EmbedSelectionChangeHandler;
  onBackClick?: () => void;
}

const urlMapping = {
  liveboard: '/liveboard',
  search: '/search',
  viz: '/viz',
  spotter: '/spotter',
  home: '/home',
};

const resetMap: Record<viewType, EmbedSelections> = {
  search: { dataSource: '', answerId: '' },
  liveboard: { pinboard: '', viz: '' },
  viz: { pinboard: '', viz: '' },
  spotter: { dataSource: '' },
  home: { pageId: Page.Home },
};

export const Header = ({
  isLoading,
  setIsLoading,
  selectedPinboard,
  selectedViz,
  selectedDataSource,
  selectedAnswer,
  selectedFullApp,
  onEmbedSelectionChange,
  onBackClick,
}: HeaderProps) => {
  const { activeView, setActiveView } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [embedLinks, setEmbedLinks] = useState(links);
  const [searchType, setSearchType] = useState<'datasource' | 'answers'>('datasource');

  useEffect(() => {
    const currentLocation = location.pathname;
    const viewPath = currentLocation.split('/')[1] || 'liveboard';
    setActiveView(viewPath as viewType);
    navigate(urlMapping[viewPath as viewType]);

    if (viewPath === 'search') {
      onSearchChange('datasource');
    }

    if (onEmbedSelectionChange) {
      onEmbedSelectionChange(resetMap[viewPath as viewType]);
    }
  }, [location.pathname]);

  const onDropdownChange = (option) => {
    setActiveView(option.id as viewType);
    const targetUrl = urlMapping[option.id] || '/';
    navigate(targetUrl);
    onEmbedSelectionChange?.(resetMap[option.id as viewType]);
  };

  const onPinboardChange = (pinboardId: string) => {
    if (onEmbedSelectionChange) {
      onEmbedSelectionChange({ pinboard: pinboardId });
    }
  };

  const onFullAppChange = (pageId: string) => {
    if (onEmbedSelectionChange) {
      onEmbedSelectionChange({ pageId: pageId });
    }
  };

  const onSearchChange = (source: 'datasource' | 'answers') => {
    setSearchType(source);
    if (onEmbedSelectionChange) {
      onEmbedSelectionChange({
        dataSource: source === 'datasource' ? selectedDataSource : undefined,
        answerId: source === 'answers' ? selectedAnswer : undefined,
        searchType: source,
      });
    }
  };

  const onVizChange = (vizId: string) => {
    if (onEmbedSelectionChange) {
      onEmbedSelectionChange({ viz: vizId, pinboard: selectedPinboard });
    }
  };

  const onDataSourceChange = (dataSourceId: string) => {
    if (onEmbedSelectionChange) {
      onEmbedSelectionChange({ dataSource: dataSourceId });
    }
  };

  const onAnswerChange = (answerId: string) => {
    if (onEmbedSelectionChange) {
      onEmbedSelectionChange({ answerId: answerId, dataSource: selectedDataSource });
    }
  };

  useEffect(() => {
    const fetchPreauthInfo = async () => {
      setIsLoading(true);
      await fetchPreauthInfoService();
      getLinks();
      setIsLoading(false);
    };
    try {
      fetchPreauthInfo();
    } catch (error) {
      setIsLoading(false);
      getLinks();
    }
  }, []);

  const getLinks = async () => {
    const infoObject = (await getInfoObject()) as any;
    const links = [...dropdownOptions];
    const configInfo = infoObject?.info?.configInfo;
    if (configInfo?.isSpotterExperienceEnabled && configInfo?.aiAnswerGenerationEnabled) {
      setEmbedLinks([...links, ...spotterLink]);
    }
    return links;
  };

  const getMainDropdown = () => {
    return (
      <>
        <Typography variant="p" color={Colors.base} className={styles['dropdown-title']}>
          {SELECT_A_COMPONENT}
        </Typography>
        <Horizontal className={styles['select-dropdown']}>
          <Select.Dropdown
            id="select-dropdown"
            optionsGroups={[{ options: embedLinks }]}
            selectedOptions={[activeView]}
            onChange={onDropdownChange}
            loading={isLoading}
          />
        </Horizontal>
      </>
    );
  };

  const getComponentSelectionDropdown = (props: any) => {
    return (
      <Vertical className={styles['select-container']}>
        <Typography variant="p" color={Colors.base} className={styles['dropdown-title']}>
          {props.dropdownTitle}
        </Typography>
        <Horizontal className={styles['select-dropdown']}>
          <div style={{ width: '100%' }}>
            <GeneralizedDropdown {...props} />
          </div>
        </Horizontal>
      </Vertical>
    );
  };

  return (
    <div className={styles['header-container']}>
      {isNewVersion() && !isAiMode() && (
        <>
          <Horizontal className={styles['header-title']}>
            <div className={styles['back-icon']} onClick={onBackClick}>
              <Icon id={'arrow-left' as IconID} size={IconSize.MEDIUM} />
            </div>
            <Typography
              variant="body-normal"
              className={styles['back-text']}
              onClick={onBackClick}
              color={Colors.base}
            >
              {BACK_BUTTON_TEXT}
            </Typography>
          </Horizontal>
          <Separator
            orientation="horizontal"
            color={'var(--rd-sys-color-border-subtle)'}
            style={{ marginBottom: '8px' }}
          />
        </>
      )}

      <Vertical>
        {getMainDropdown()}
        {activeView === 'search' &&
          getComponentSelectionDropdown({
            onChange: onSearchChange,
            selectedValue: searchType,
            dropdownTitle: SELECT_SAVED_ANSWER_OR_DATA_SOURCE,
            title: 'Search data',
            type: 'component-selection',
          })}
        {(activeView === 'search' || activeView === 'spotter') &&
          getComponentSelectionDropdown({
            onChange:
              searchType === 'datasource' || activeView === 'spotter'
                ? onDataSourceChange
                : onAnswerChange,
            selectedValue:
              searchType === 'datasource' || activeView === 'spotter'
                ? selectedDataSource
                : selectedAnswer,
            dropdownTitle:
              searchType === 'datasource' || activeView === 'spotter'
                ? SELECT_A_DATA_SOURCE
                : SELECT_A_SAVED_ANSWER,
            title:
              searchType === 'datasource' || activeView === 'spotter' ? 'Data Source' : 'Answer',
            type: searchType === 'datasource' || activeView === 'spotter' ? 'datasource' : 'answer',
          })}
        {(activeView === 'liveboard' || activeView === 'viz') &&
          getComponentSelectionDropdown({
            onChange: onPinboardChange,
            selectedValue: selectedPinboard,
            dropdownTitle: SELECT_A_LIVEBOARD,
            title: 'Liveboard',
            type: 'liveboard',
          })}

        {activeView === 'viz' &&
          selectedPinboard &&
          getComponentSelectionDropdown({
            onChange: onVizChange,
            selectedValue: selectedViz,
            dropdownTitle: SELECT_A_VISUALIZATION,
            title: 'Visualization',
            type: 'visualization',
            liveboardId: selectedPinboard,
            disabled: !selectedPinboard,
          })}

        {activeView === 'home' &&
          getComponentSelectionDropdown({
            onChange: onFullAppChange,
            selectedValue: selectedFullApp,
            dropdownTitle: SET_ACTIVE_LEFT_NAVIGATION_ITEM,
            title: 'Home',
            type: 'fullapp',
          })}
      </Vertical>
    </div>
  );
};
